import { Component, Input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonInput,
  IonSpinner,
  IonIcon,
  IonText,
  ModalController
} from '@ionic/angular/standalone';
import { OtpInputComponent } from '@appchezandre/angular-ui';
import { StudentVerificationService } from '../../services/http/student-verification.service';
import { AuthService } from '../../services/http/auth.service';
import { StorageService } from '../../services/storage.service';

/**
 * Modal bloquante pour la saisie obligatoire de l'email étudiant
 * Utilisée pour:
 * - Migration: Anciens utilisateurs avec certificat mais sans email
 * - Renouvellement: Utilisateurs dont l'accès a expiré
 *
 * Cette modal ne peut PAS être fermée - l'utilisateur DOIT compléter le processus
 */
@Component({
  selector: 'app-student-email-modal',
  standalone: true,
  imports: [
    FormsModule,
    IonContent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonInput,
    IonSpinner,
    IonIcon,
    OtpInputComponent
  ],
  templateUrl: './student-email-modal.component.html',
  styleUrls: ['./student-email-modal.component.scss']
})
export class StudentEmailModalComponent {
  @Input() reason: 'migration' | 'renouvellement' = 'renouvellement';

  // State management
  currentStep = signal<'email' | 'otp'>('email');
  studentEmail = signal('');
  otpCode = signal('');
  isLoading = signal(false);
  errorMessage = signal('');
  isDomainNotRecognized = signal(false);

  constructor(
    private modalController: ModalController,
    private studentVerificationService: StudentVerificationService,
    private authService: AuthService,
    private storageService: StorageService
  ) {}

  /**
   * Envoie le code OTP à l'email étudiant saisi
   */
  async sendOTP() {
    const email = this.studentEmail().trim().toLowerCase();

    if (!email) {
      this.errorMessage.set('Veuillez entrer votre email étudiant');
      return;
    }

    // Validation basique du format email
    if (!email.includes('@') || !email.includes('.')) {
      this.errorMessage.set('Format d\'email invalide');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');
    this.isDomainNotRecognized.set(false);

    try {
      await this.studentVerificationService.sendOtp(email);
      this.currentStep.set('otp');
    } catch (error: any) {
      console.error('Erreur envoi OTP:', error);
      const errorMsg = error.error?.message || 'Erreur lors de l\'envoi du code. Veuillez réessayer.';
      this.errorMessage.set(errorMsg);

      // Detecter si c'est une erreur de domaine non reconnu
      if (errorMsg.includes('n\'est pas reconnue comme un email universitaire')) {
        this.isDomainNotRecognized.set(true);
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Vérifie le code OTP et met à jour l'email étudiant
   */
  async verifyOTP() {
    const email = this.studentEmail().trim().toLowerCase();
    const code = this.otpCode().trim();

    if (!code || code.length !== 6) {
      this.errorMessage.set('Le code doit contenir 6 chiffres');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');

    try {
      // Étape 1: Vérifier le code OTP
      const verifyResult = await this.studentVerificationService.verifyOtp(email, code);

      if (!verifyResult.valid) {
        this.errorMessage.set('Code invalide ou expiré');
        this.isLoading.set(false);
        return;
      }

      // Étape 2: Mettre à jour l'email étudiant
      const response = await this.authService.updateStudentEmail({
        email: email,
        code: code
      });

      // Étape 3: Mettre à jour le token et le profil utilisateur
      if (response.access_token) {
        const rememberMe = this.storageService.isRememberMeActive();
        const refreshToken = this.storageService.getRefreshToken();
        this.storageService.setToken(response.access_token, rememberMe, refreshToken ?? undefined);

        const fullProfile = await this.authService.getProfile();
        this.storageService.setUser(fullProfile);
      }

      // Fermer la modal avec succès
      await this.modalController.dismiss({ success: true });
    } catch (error: any) {
      console.error('Erreur vérification OTP:', error);
      this.errorMessage.set(
        error.error?.message || 'Erreur lors de la vérification. Veuillez réessayer.'
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Callback quand l'utilisateur change le code OTP
   */
  onOtpChange(otp: string) {
    this.otpCode.set(otp);
  }

  /**
   * Callback quand l'OTP est complet (6 chiffres)
   * Déclenche automatiquement la vérification
   */
  onOtpComplete() {
    this.verifyOTP();
  }

  /**
   * Retour à l'étape saisie email
   */
  goBackToEmail() {
    this.currentStep.set('email');
    this.otpCode.set('');
    this.errorMessage.set('');
  }

  /**
   * Message explicatif basé sur la raison de la modal
   */
  get explanationMessage(): string {
    if (this.reason === 'migration') {
      return 'Votre compte a été vérifié avec un certificat de scolarité. Pour continuer à utiliser La BASE, vous devez maintenant valider votre email étudiant universitaire.';
    } else {
      return 'Votre accès a expiré. Veuillez renseigner votre email étudiant universitaire pour continuer à passer des commandes.';
    }
  }
}
